'use client';

import { useEffect, useState } from 'react';
import { Listing, ListingItem } from '@/components/section/listing/listing';
import type { FilterConfig } from '@/components/section/listing/listingFilters';
import styles from './ListingExperience.module.scss';

const LISTING_TYPE_MAP: Record<string, string> = {
  showEvent: 'event',
  showWebinar: 'webinar',
  showCaseStudy: 'case-study',
  showBlog: 'blog',
  showNews: 'news',
  showInsight: 'insight',
  showWhitepaper: 'whitepaper',
};

type ListingExperienceProps = {
  showEvent: boolean;
  showWebinar: boolean;
  showCaseStudy: boolean;
  showBlog: boolean;
  showNews: boolean;
  showInsight: boolean;
  showWhitepaper: boolean;
  filterTypes: string;
  showPastDate: boolean;
};

type ListingData = {
  items: ListingItem[];
  filters: FilterConfig[];
};

export const ListingExperience = ({
  showEvent,
  showWebinar,
  showCaseStudy,
  showBlog,
  showNews,
  showInsight,
  showWhitepaper,
  filterTypes,
  showPastDate,
}: ListingExperienceProps) => {
  const [data, setData] = useState<ListingData | null>(null);

  const toggles = {
    showEvent,
    showWebinar,
    showCaseStudy,
    showBlog,
    showNews,
    showInsight,
    showWhitepaper,
  };

  useEffect(() => {
    const types = Object.entries(toggles)
      .filter(([, enabled]) => enabled)
      .map(([key]) => LISTING_TYPE_MAP[key])
      .join(',');

    if (!types) return;

    const filters = (filterTypes ?? '')
      .split(',')
      .map((f) => f.trim())
      .filter(Boolean)
      .join(',');

    const params = new URLSearchParams({ types });
    if (filters) params.set('filters', filters);

    fetch(`/api/listing?${params}`)
      .then((res) => res.json())
      .then((json: ListingData) => setData(json))
      .catch(() => setData(null));
  }, [
    showEvent,
    showWebinar,
    showCaseStudy,
    showBlog,
    showNews,
    showInsight,
    showWhitepaper,
    filterTypes,
  ]);

  if (!data || !data.items.length) return null;

  return (
    <Listing
      items={data.items}
      filters={data.filters}
      showPastDate={showPastDate}
      className={styles.section}
    />
  );
};
